import { api } from './api';
import * as native from './native';

/**
 * Push notifications.
 *
 * iOS is the constraint that shapes this. Safari grants no push to an ordinary
 * tab: the app must have been added to the Home Screen, and permission can only
 * be requested from a real user gesture. So the UI has to be able to say which
 * of those is missing, rather than offering a switch that silently does
 * nothing — hence `status()` returning a reason instead of a boolean.
 */

export type PushState =
  | 'unsupported'      // No service worker or Push API at all.
  | 'needs-install'    // iOS Safari tab — must be added to the Home Screen first.
  | 'not-configured'   // Server has no VAPID keys.
  | 'denied'           // The user said no; only Settings can undo it.
  | 'off'              // Available, not yet enabled.
  | 'on';

export interface PushStatus {
  state: PushState;
  devices: number;
}

/** True when running as an installed app rather than a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS predates the standard and still reports it here.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export async function status(): Promise<PushStatus> {
  // The App Store build has no service worker and no Push API — it gets a
  // device token from Apple instead. Falling through to the checks below would
  // report the one place push works best as "unsupported".
  if (native.isNative()) {
    const server = await api.get<{ apnsConfigured: boolean; natives: number }>('/push/status');
    if (!server.apnsConfigured) return { state: 'not-configured', devices: 0 };
    return { state: server.natives > 0 ? 'on' : 'off', devices: server.natives };
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // On iOS the Push API is genuinely absent in a tab and appears once
    // installed, so "unsupported" there is really "not installed yet".
    return { state: isIos() ? 'needs-install' : 'unsupported', devices: 0 };
  }

  const server = await api.get<{ configured: boolean; devices: number }>('/push/status');
  if (!server.configured) return { state: 'not-configured', devices: 0 };

  if (Notification.permission === 'denied') return { state: 'denied', devices: server.devices };

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing && server.devices > 0) return { state: 'on', devices: server.devices };

  if (isIos() && !isStandalone()) return { state: 'needs-install', devices: 0 };
  return { state: 'off', devices: server.devices };
}

/** The VAPID key is base64url over the wire and a byte array in the API. */
function decodeKey(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Must be called from a user gesture — iOS rejects a permission prompt that
 * was not asked for by a tap.
 */
export async function enable(): Promise<PushState> {
  if (native.isNative()) {
    const result = await native.registerForPush();
    return result === 'on' ? 'on' : result === 'denied' ? 'denied' : 'not-configured';
  }

  const server = await api.get<{ configured: boolean; publicKey: string | null }>('/push/status');
  if (!server.configured || !server.publicKey) return 'not-configured';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Non-negotiable on every current browser: a push must show a
      // notification, which is also the honest behaviour for a finance alert.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(server.publicKey),
    }));

  await api.post('/push/subscribe', subscription.toJSON());
  return 'on';
}

export async function disable(): Promise<void> {
  if (native.isNative()) {
    await native.unregisterPush();
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  // Tell the server first: a subscription dropped locally but left on the
  // server would keep receiving pushes with nothing to display them.
  await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
  await subscription.unsubscribe();
}

export async function sendTest(): Promise<number> {
  return (await api.post<{ delivered: number }>('/push/test')).delivered;
}
